import { create } from 'zustand';
import type {
  CreatePromotionRequest,
  PromotionDto,
  UpdatePromotionRequest,
} from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';

/**
 * Automatic discounts (API → Store → Render, ADR-008).
 *
 * Deliberately separate from the coupons store: an automatic discount and a
 * code answer different questions, and merging them would mean every screen
 * branching on which kind it is holding.
 *
 * Owner-only on the API, so the page asks for nothing until it knows who is
 * looking. The list's error and the editor's are kept apart, so a refusal
 * lands beside the form that met it.
 */

interface PromotionsState {
  items: PromotionDto[];
  loading: boolean;
  saving: boolean;
  /** The list's own trouble: loading, stopping, starting again. */
  error: string | null;
  /** Why the open editor's save was refused. */
  saveError: string | null;

  load: () => Promise<void>;
  create: (body: CreatePromotionRequest) => Promise<boolean>;
  update: (id: string, body: UpdatePromotionRequest) => Promise<boolean>;
  /** Switched off, never deleted: orders record what discounted them. */
  deactivate: (id: string) => Promise<boolean>;
  /** Back on, inside whatever dates it already has. */
  activate: (id: string) => Promise<boolean>;
  clearSaveError: () => void;
}

export const usePromotionsStore = create<PromotionsState>((set, get) => ({
  items: [],
  loading: false,
  saving: false,
  error: null,
  saveError: null,

  async load() {
    set({ loading: true, error: null });

    try {
      const page = await withSession(() => adminApi.promotions.list());
      set({ items: page.items, loading: false });
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load the automatic discounts.') });
    }
  },

  async create(body) {
    set({ saving: true, saveError: null });

    try {
      const created = await withSession(() => adminApi.promotions.create(body));
      set({ saving: false, items: [created, ...get().items] });
      return true;
    } catch (error) {
      // The API's refusals name the problem — an offer scoped to nothing, a
      // window that ends before it starts — so they are shown as they arrive.
      set({ saving: false, saveError: errorMessage(error, 'Could not save this automatic discount.') });
      return false;
    }
  },

  async update(id, body) {
    set({ saving: true, saveError: null });

    try {
      const updated = await withSession(() => adminApi.promotions.update(id, body));
      set({ saving: false, items: replace(get().items, updated) });
      return true;
    } catch (error) {
      set({ saving: false, saveError: errorMessage(error, 'Could not save this automatic discount.') });
      return false;
    }
  },

  async deactivate(id) {
    set({ saving: true, error: null });

    try {
      const updated = await withSession(() => adminApi.promotions.deactivate(id));
      set({ saving: false, items: replace(get().items, updated) });
      return true;
    } catch (error) {
      set({ saving: false, error: errorMessage(error, 'Could not stop this automatic discount.') });
      return false;
    }
  },

  async activate(id) {
    set({ saving: true, error: null });

    try {
      const updated = await withSession(() => adminApi.promotions.update(id, { isActive: true }));
      set({ saving: false, items: replace(get().items, updated) });
      return true;
    } catch (error) {
      set({ saving: false, error: errorMessage(error, 'Could not start this automatic discount again.') });
      return false;
    }
  },

  clearSaveError() {
    set({ saveError: null });
  },
}));

function replace(items: PromotionDto[], updated: PromotionDto): PromotionDto[] {
  return items.map((item) => (item.id === updated.id ? updated : item));
}
